import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { SettingResult, SettingsService } from '../../core/settings/settings.service';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IsFullWidthRowParams } from "ag-grid-community";
import { ValueCellComponent } from "./value-cell/value-cell.component";
import { CategoryCellComponent } from "./category-cell/category-cell.component";
import { VideoProfileCellComponent } from "./video-profile-cell/video-profile-cell.component";
import {
  extractProfileValues,
  profileBadgeLabel,
  VideoProfileValues,
  VIDEO_PROFILE_SETTING_NAME,
  VIDEO_PROFILE_TARGET_SETTINGS,
} from "../../core/settings/video-profile";

@Component({
  selector: 'settings-grid',
  templateUrl: './settings-grid.component.html',
  styleUrls: ['./settings-grid.component.scss']
})

export class SettingsGridComponent implements OnChanges {

  @Input()
  settings!: SettingResult[];

  @Input()
  searchedSetting!: string;

  @Input()
  editable: boolean = false;

  @Output()
  settingHover: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  settingDehover: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  gridReady: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  settingsReady: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  settingClicked: EventEmitter<SettingResult> = new EventEmitter<SettingResult>();

  columnDefs: ColDef[] = [
    {
      headerName: 'Setting',
      field: 'label',
      flex: 1,
      sortable: false
    },
    {
      headerName: 'Value',
      field: 'value',
      flex: 1,
      sortable: false,
      cellRendererSelector: (params) =>
        params.data?.isVideoProfile
          ? {
              component: VideoProfileCellComponent,
              params: { getIsClickable: () => this.editable },
            }
          : {
              component: ValueCellComponent,
              params: { getIsClickable: () => this.editable },
            },
      onCellClicked: (e) => {
        if(this.editable) {
          this.settingClicked.emit(e.data.value);
        }
      },
      cellRendererParams: {
        getIsClickable: () => this.editable
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true
  };

  gridOptions: GridOptions = {
    getRowId: row => !row.data.isCategoryRow ? row.data.name : row.data.name + ' category row',
    isFullWidthRow: params => params.rowNode.data.isCategoryRow,
    getRowHeight: params => params.data?.isVideoProfile ? 230 : undefined,
    alwaysShowVerticalScroll: true,
    suppressRowClickSelection: true,
    suppressLoadingOverlay: true,
    suppressRowHoverHighlight: true,
    domLayout: "normal",
    fullWidthCellRenderer: CategoryCellComponent,
    components: {
      valueCellRenderer: ValueCellComponent,
      videoProfileCellRenderer: VideoProfileCellComponent,
    }
  };

  displayedSettings!: GridSetting[];

  public gridApi!: GridApi<GridSetting>;

  constructor(private settingsService: SettingsService) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    let mapped = this.settings?.map<GridSetting>((s: SettingResult) => ({
      name: s.name,
      label: this.settingsService.getSettingType(s.name).label,
      value: s,
      category: s.category,
      isCategoryRow: false
    }));

    if (!mapped) {
      this.gridApi?.showLoadingOverlay();
      return;
    }

    mapped = this.collapseVideoProfile(mapped);

    if (mapped.length === 0) {
      this.displayedSettings = [];
      return;
    }

    mapped.sort((a, b) => {
      if (a.category > b.category) return 1;
      if (a.category < b.category) return -1;
      return a.label.localeCompare(b.label);
    });

    this.displayedSettings = this.insertCategoryRows(mapped);
  }

  /**
   * Collapses the four real SF400 Encoder/Source rows into a single
   * "HD Video Profile - SF400" row (matching the LDCC mockup). The individual
   * rows are removed so only the grouped row is shown.
   */
  private collapseVideoProfile(settings: GridSetting[]): GridSetting[] {
    // Match on the human identifier (Label), since the grid rows carry the
    // API's Name (which may differ from Label). This groups all four rows
    // consistently regardless of Name/Label decoupling.
    const targetLabels: string[] = Object.values(VIDEO_PROFILE_TARGET_SETTINGS);
    const targetRows = settings.filter(
      (g) => targetLabels.includes(g.label) || targetLabels.includes(g.name),
    );

    if (targetRows.length === 0) {
      return settings;
    }

    // Key by both Name and Label: rows are matched on either (Name/Label can be
    // decoupled), and extractProfileValues looks up by the canonical identifier,
    // so populating both avoids a missed lookup when Name !== Label.
    const valueByName = new Map<string, string>();
    targetRows.forEach((g) => {
      valueByName.set(g.name, g.value?.value);
      valueByName.set(g.label, g.value?.value);
    });
    const raw: VideoProfileValues | null = extractProfileValues(valueByName);
    if (!raw) {
      return settings;
    }
    // STATIC_QP values always show as Custom; only BITRATE values can match a recommended profile.
    const values = raw;

    
    const resultFor = (identifier: string): SettingResult | undefined =>
      targetRows.find((g) => g.label === identifier || g.name === identifier)?.value;
    const withPending = (
      displayValue: string,
      identifier: string,
      convert: (v: string) => string,
    ): string => {
      const r = resultFor(identifier);
      if (!r?.isPending) {
        return displayValue;
      }
      if (r.lastKnownValue === undefined || r.lastKnownValue === null) {
        return `${displayValue} (value is pending, current value: undefined, awaiting initial settings sync)`;
      }
      return `${displayValue} (value is pending, current value: ${convert(r.lastKnownValue)})`;
    };
    const targets = VIDEO_PROFILE_TARGET_SETTINGS;
    const displayValues: VideoProfileValues = {
      encoderExterior: withPending(values.encoderExterior, targets.encoderExterior, (v) => v),
      encoderInterior: withPending(values.encoderInterior, targets.encoderInterior, (v) => v),
      sourceExterior: withPending(values.sourceExterior, targets.sourceExterior, (v) => v),
      sourceInterior: withPending(values.sourceInterior, targets.sourceInterior, (v) => v),
    };

    const remaining = settings.filter(
      (g) => !targetLabels.includes(g.label) && !targetLabels.includes(g.name),
    );
    const profileType = this.settingsService.getSettingType(VIDEO_PROFILE_SETTING_NAME);
    const templateRow = targetRows[0];

    // The grouped row is "pinned" when ANY of the four underlying settings is
    // authoritative at the viewed level — i.e. the profile was modified here.
    // The cell compares these IDs against the entity in the route.
    const authoritativeEntityIds = targetRows.map(
      (g) => g.value?.authoritativeEntityId,
    );

    // synthetic SettingResult so a click opens the modal on the virtual setting;
    // carry the four current values so the modal can pre-select the matching profile
    const syntheticValue = {
      ...templateRow.value,
      name: VIDEO_PROFILE_SETTING_NAME,
      videoProfile: values,
    } as SettingResult & { videoProfile: VideoProfileValues };

    remaining.push({
      name: VIDEO_PROFILE_SETTING_NAME,
      label: profileType?.label ?? VIDEO_PROFILE_SETTING_NAME,
      value: syntheticValue,
      category: templateRow.category,
      isCategoryRow: false,
      isVideoProfile: true,
      videoProfile: {
        ...displayValues,
        profileLabel: profileBadgeLabel(values),
        authoritativeEntityIds,
      },
    });

    return remaining;
  }

  private insertCategoryRows(settings: GridSetting[]): GridSetting[] {
    let i = settings.length;
    let currentCategory = settings[i - 1].category;

    while (i--) {

        if (settings[i].category !== currentCategory) {
          settings.splice(i + 1, 0, {
            name: currentCategory,
            label: currentCategory,
            value: settings[i + 1].value,
            category: currentCategory,
            isCategoryRow: true
          });

          currentCategory = settings[i].category;
        }
    }

    settings.splice(0, 0, {
      name: currentCategory,
      label: currentCategory,
      value: settings[0].value,
      category: currentCategory,
      isCategoryRow: true
    });
    return settings;
  }

  scrollToSetting(settingName: string) {

    const row = this.gridApi?.getRowNode(settingName);

    if (!row) return;

    row.setSelected(true);
    this.gridApi.ensureNodeVisible(row, "middle");
  }

  onSettingsUpdated(event: any) {
    if (!this.gridApi) return;

    this.gridApi.forEachNode((n) => {
      n.onMouseEnter = () => {
        n.selectThisNode(true);
        const hoveredSetting = this.settings.find(s => s.name === n.id)
        if(hoveredSetting != null) {
          this.settingHover.emit(hoveredSetting.authoritativeEntityId);
        }
      }

      n.onMouseLeave = () => {
        n.setSelected(false);
        this.settingDehover.emit();
      }
    });

    this.settingsReady.emit();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.showLoadingOverlay();

    window.addEventListener('resize', function () {
      setTimeout(function () {
        params.api.sizeColumnsToFit();
      });
    });
    this.gridApi.sizeColumnsToFit();
    this.gridReady.emit();
  };

}

interface GridSetting {
  name: string;
  label: string;
  value: SettingResult;
  category: string;
  isCategoryRow: boolean;
  isVideoProfile?: boolean;
  videoProfile?: VideoProfileValues & {
    profileLabel: string;
    authoritativeEntityIds?: (string | undefined)[];
  };
}
